<template>
  <div class="FileUuidDownloadLink d-flex w-100 justify-content-between" :class="downloading ? 'FileUuidDownloadLink--downloading' : ''">
    <span
      v-if="fileRecordFailedToLoad"
    >Bestandsinformatie kon niet worden opgehaald</span>
    <span
      v-else-if="! isThisFileAvailable || retrying"
    >Bestandsinformatie wordt opgehaald...</span>
    <span
      v-else-if="! isAvailableForDownload"
    >{{
      file.getFullFilename()
    }} ({{ (retrying ? 'bezig met verificatie...' : 'Onbeschikbaar') }})</span>
    <span
      v-else
      class="download-link text-button"
      @click="handleDownload"
    >
      <b-icon
        v-show="icon && ! downloading"
        icon="download"
      /><b-icon 
        v-show="downloading"
        icon="arrow-repeat" 
        animation="spin"
        class="mr-1"
      />
      <span class="text-button__label">{{
        label || file.getFullFilename()
      }}</span>
    </span>

    <span 
      class="download-link text-button"
      v-if="! disconnecting && ! disabled && disconnect && isThisFileAvailable"
      @click="handleDisconnectFile">
      <b-icon 
        v-if="icon"
        icon="x" 
      /><span class="text-button__label">Wissen</span>
    </span>
    <span v-if="disconnecting">
      <b-icon 
        icon="arrow-repeat" 
        animation="spin"
      /> Bezig...
    </span>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { saveAs } from 'file-saver'

/**
 * List of UUID based download links
 *  TODO: Offer retry btn on failure to get File data
 */
export default {
  name: 'FileUuidDownloadLink',
  props: {
    uuid: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null
    },
    icon: {
      type: Boolean,
      default: false
    },
    disconnect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    filenameprefix: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      retrying: false,
      disconnecting: false,
      downloading: false
    }
  },
  computed: {
    ...mapGetters('files', [
      'isFileAvailable',
      'getFileByUuid',
      'getFileFailedToLoad',
    ]),
    /**
     * @return {boolean}
     */
    isThisFileAvailable() {
      return this.isFileAvailable({
        uuid: this.uuid,
      })
    },
    /**
     *
     */
    file() {
      if (! this.isThisFileAvailable) {
        return null
      }
      return this.getFileByUuid({ uuid: this.uuid })
    },
    /**
     * TODO: CBAPI - We're ignoring whether the actual file is available
     *  The callback to the DB takes longer to process than getting the file data
     *  after when uploading a new file. This means we get a 'false' flag for 
     *  files that you've just uploaded. This would be sorted out with Websockets, 
     *  which we do not yet have at this time.
     * 
     * @return {boolean}
     */
    isAvailableForDownload() {
      if (! this.isThisFileAvailable) {
        return null
      }
      return this.file?.isAvailableForDownload() || null
    },
    /**
     * @returns {boolean}
     */
    fileRecordFailedToLoad() {
      return this.getFileFailedToLoad({
        uuid: this.uuid,
      })
    },
  },
  async created() {
    await this.loadFileInformation()
  },
  methods: {
    ...mapActions('files', [
      'loadFileInformationByUuid'
    ]),

    async loadFileInformation({ retry = false } = { retry: false }) {
      if (retry || ! this.isThisFileAvailable) {
        await this.loadFileInformationByUuid({
          uuid: this.uuid,
          force: retry
        })
      }

      if (
        this.isThisFileAvailable
        && this.file?.hasNotYetBeenChecked() 
        // Note: this method must NOT be placed in a computed or cached in any other way
        && this.file?.hasRecentTS() 
      ) {
        this.retrying = true

        // Refresh the record info after 500ms 
        setTimeout(async () => {
          await this.loadFileInformation({ retry: true })
        }, 500)
      } else {
        this.$nextTick(() => {
          this.retrying = false
        })
      }
    },

    /**
     * The uuid based file download
     */
    handleDownload: async function() {
      try {
        if (this.downloading) return 

        if (! this.isAvailableForDownload) {
          this.$notify({
            type: 'error',
            title: 'Bestand is niet beschikbaar',
            message: 'Dit bestand is niet (meer) beschikbaar. Het uploaden kan zijn mislukt, of het bestand kan zijn opgeschoond ivm AVG.'
          })
          return 
        }
        this.downloading = true

        const response = await this.$cbapi.files.getDownloadLinkByUuid({
          uuid: this.uuid,
        })

        if (! response || ! response.link) {
          throw new Error('missing url')
        }

        // Generate the filename
        const filenameprefix = this.filenameprefix ? `${this.filenameprefix}-` : ''
        const filename = `${filenameprefix}${this.file.filename}.${this.file.extension}`

        // Chrome, FF, Edge
        if (window.browser && window.browser.downloads) {
          window.browser.downloads.download({ 
            url: response.link, 
            filename
          })
        } else { // Safari, etc.
          const res = await fetch(response.link)
          const blob = await res.blob()
          
          saveAs(blob, filename)
        }

        this.downloading = false
      } catch (e) {
        this.downloading = false
        this.$bvModal.show('file-error')
      }
    },
    handleDisconnectFile: function() {
      this.disconnecting = true
      this.$emit('disconnect', { uuid: this.uuid })
    },
  },
}

</script>

<style lang="scss">

.FileUuidDownloadLink {

  
  a:hover, .download-link:hover {
    cursor: pointer;
    text-decoration: underline;
  }

  &--downloading {
    a:hover, .download-link:hover {
      cursor: not-allowed;
      text-decoration: none;
    }
  }
  
}
</style>
